import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { AnimatedExplainer } from "@/components/AnimatedExplainer";
import { Chapter, Prose, TheoryBox, MTQuote, Glossary, Term } from "@/components/mt/MTBox";
import { NoteImage } from "@/components/mt/NoteImage";
import { ChordBuilder } from "@/components/mt/ChordBuilder";
import { NextUp } from "@/components/Callouts";

export default function MusicTheory22() {
  const { lesson, course, accent } = findLesson("music-theory", "22");
  return (
    <LessonShell
      course={course}
      courseSlug="music-theory"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <AnimatedExplainer
        src="/animations/music-theory/MT22-Jazz-Extensions-Animated.mp4"
        caption="Jazz extensions — stack the 9, 11, 13 above the 7th"
        hint="≈ 32 sec · watch first"
      />
      <Chapter number="22" eyebrow="Music Theory · Chapter Twenty-two" title="The chord keeps stacking">
        <Prose>
          <p>
            We've stacked thirds twice and got a triad. Three times and we got a seventh
            chord. Stack a <em>fourth</em> third on top of a seventh, then a fifth, then a
            sixth — and we arrive in the world of <strong>jazz extensions</strong>: the 9th,
            the 11th, and the 13th. After 13 we run out of new notes; we're already covering
            every degree of the scale.
          </p>
          <p>
            Why call them 9, 11, 13 rather than 2, 4, 6? Because by the time you're stacking
            this far, the lower intervals are already in the chord — you assume there's a
            7th sitting there. Calling the new note a <strong>9</strong> rather than a 2
            says, "this is the next octave up, sitting <em>above</em> the 7th, not crammed
            into the same octave as the root". It's a notation about <em>where</em> the note
            lives in the stack.
          </p>
          <MTQuote>
            The reason why they say 9 is because it assumes that there's already a seventh,
            and that means a four-note chord.
          </MTQuote>
        </Prose>
        <NoteImage
          src="/notes/music-theory/finals/22a-extensions-9-11-13-with-alterations.png"
          caption="Jason's note: jazz extensions 9, 11, 13 are 'played along with sevenths' — i.e. you don't use them on bare triads. Each extension comes in three flavours: natural, ♭, or ♯. So 9 / ♭9 / ♯9 from the same root. The chart maps each flavour to its actual note name in C7."
        />
      </Chapter>

      <Chapter eyebrow="The natural extensions" title="9, 11 and 13 — three new colours">
        <Prose>
          <p>
            Start from a Cmaj7 (C E G B). Build the C major scale on top of that and the
            extensions appear in order:
          </p>
          <ul className="space-y-1 text-[14px]">
            <li>The <strong>9</strong> is <strong>D</strong> — a major 2nd above the root, octave up.</li>
            <li>The <strong>11</strong> is <strong>F</strong> — a perfect 4th above the root, octave up.</li>
            <li>The <strong>13</strong> is <strong>A</strong> — a major 6th above the root, octave up.</li>
          </ul>
          <p>
            Stack the whole lot — root, 3, 5, 7, 9, 11, 13 — and you've laid the entire C
            major scale out vertically as a chord. That's the secret of jazz harmony: every
            "tall" chord is just the parent scale rebuilt as a stack of thirds.
          </p>
        </Prose>

        <TheoryBox label="The full extended stack">
          <div className="text-center font-mt text-xl text-amber-200">
            <strong>1 &nbsp; 3 &nbsp; 5 &nbsp; 7 &nbsp; 9 &nbsp; 11 &nbsp; 13</strong>
          </div>
          <div className="mt-2 text-center text-[13px] text-nsm-dim">
            (For C major: C E G B D F A — all seven notes of the scale, stacked in thirds.)
          </div>
        </TheoryBox>
      </Chapter>

      <Chapter eyebrow="The altered extensions" title="Flatten or sharpen — and the chord transforms">
        <Prose>
          <p>
            Each extension can be raised or lowered by a semitone, giving us a richer palette
            of <strong>altered tensions</strong>. These are the colours that make jazz sound
            jazzy. The names you'll see most often:
          </p>
          <ul className="space-y-1 text-[14px]">
            <li><strong>♭9</strong> — flat 9 (e.g. D♭ over C). Dark, heavy. Famously resolves into a minor i chord.</li>
            <li><strong>♯9</strong> — sharp 9 (e.g. D♯ over C). The "Hendrix chord" — used in <em>Purple Haze</em>. Bluesy, gritty.</li>
            <li><strong>♯11</strong> — sharp 11 (e.g. F♯ over C). Also called <em>Lydian sound</em>. Floating, dreamy.</li>
            <li><strong>♭13</strong> — flat 13 (e.g. A♭ over C). Often called a <strong>♯5</strong> in this context. Tense, unresolved.</li>
          </ul>
          <p>
            Note the alternative names. In jazz, a <strong>♯11</strong> is sometimes written
            as <strong>♭5</strong> if the player is supposed to <em>replace</em> the natural
            5 (rather than add F♯ alongside G). And a <strong>♭13</strong> is sometimes
            written as <strong>♯5</strong> for the same reason. The notation tells the
            arranger whether to keep or drop the natural fifth.
          </p>
        </Prose>

        <Glossary>
          <Term name="9" def="The 2nd of the scale, voiced an octave up. Implied to live above an existing 7th." />
          <Term name="♭9" def="The flat 2nd, voiced an octave up. Most often heard on dominant 7 chords resolving to minor." />
          <Term name="♯9" def="The sharp 2nd / minor 3rd, voiced up. The 'Jimi Hendrix' chord on a dominant 7." />
          <Term name="11" def="The perfect 4th, voiced up. Sounds suspended; conflicts with a major 3rd, so usually appears on minor or dominant chords." />
          <Term name="♯11" def="The Lydian colour. Sits beautifully on maj7 chords (Cmaj7♯11) and dominants (C7♯11) without clashing." />
          <Term name="13" def="The major 6th, voiced up. Open and lush. Default extension on dominant 7 chords." />
          <Term name="♭13" def="The minor 6th, voiced up. Tense; often called ♯5 if it replaces the natural fifth." />
        </Glossary>
      </Chapter>

      <Chapter eyebrow="Where each tension lives best" title="Dominants love alterations">
        <Prose>
          <p>
            Some chords accept any tension; others are pickier. The clear winner for taking
            <em>everything</em> is the <strong>dominant 7 chord</strong>. A dominant 7 is
            already unstable — it wants to resolve. Pile every alteration on top and it
            still works, because the whole chord is heading somewhere anyway.
          </p>
          <p>
            That's why you see things like:{" "}
            <span className="font-mono text-amber-300">C7♭9</span>,{" "}
            <span className="font-mono text-amber-300">C7♯9</span>,{" "}
            <span className="font-mono text-amber-300">C7♯11</span>,{" "}
            <span className="font-mono text-amber-300">C13♭9</span>, even{" "}
            <span className="font-mono text-amber-300">C7♭9♯9♯11♭13</span> in extreme cases.
            Each alteration is a different flavour of how the dominant pulls home.
          </p>
          <p>
            Major 7 chords are pickier. They naturally accept the <strong>9</strong> and
            the <strong>♯11</strong> beautifully (Cmaj9, Cmaj7♯11) — the ♯11 gives that
            dreamy Lydian colour. But a natural 11 on a maj7 clashes badly (the F sits a
            semitone above the E, the major 3rd) so it's almost never used.
          </p>
          <p>
            Minor 7 chords love the <strong>9</strong> and the <strong>11</strong>. The
            13 sits well too (Dm13). Minor 7♭5 chords are the picky ones — they like the
            11 and not much else.
          </p>
        </Prose>

        <TheoryBox label="Quick-reference: which extension on which chord">
          <ul className="space-y-1 text-[14px]">
            <li><strong>maj7</strong> — natural 9, ♯11, 13. Avoid natural 11.</li>
            <li><strong>dom7</strong> — anything goes. ♭9, ♯9, 9, ♯11, 13, ♭13.</li>
            <li><strong>m7</strong> — 9, 11, 13. (The natural 11 sits well over a m7.)</li>
            <li><strong>m7♭5</strong> — 11 is the safest tension. Others depend on the resolution.</li>
          </ul>
        </TheoryBox>
      </Chapter>

      <Chapter eyebrow="Voicing" title="You don't have ten fingers — choose wisely">
        <Prose>
          <p>
            Here's the practical secret nobody tells you upfront: you almost never play{" "}
            <em>all</em> the notes. A C13♯11 contains seven notes; a human pianist has ten
            fingers but uses two hands and most of them are reserved for the melody. So
            extension chords are <strong>voiced</strong> — you pick the few notes that tell
            the story and leave the rest implied.
          </p>
          <p>
            The two notes you almost always keep are the <strong>3rd</strong> and the{" "}
            <strong>7th</strong> — these define the chord's quality. The 5th can usually
            be dropped. The root can sometimes be dropped (the bass player has it). And
            then you add one or two extensions on top — the 9, the 13, the ♭9 — whichever
            colour the moment calls for.
          </p>
          <p>
            A jazz pianist's left hand might play just <strong>3 and 7</strong>. The right
            hand plays the melody plus one extension. That's a "shell voicing" — economical
            but unmistakably jazz.
          </p>
        </Prose>

        <MTQuote>
          You don't have this many fingers on the human body to play so many notes. So you
          have to — whenever you're looking at a 9, 11 and a 13 scenario, when you want to
          use these tensions, my advice to you very quickly in a nutshell would be: your
          melody demands it.
        </MTQuote>
      </Chapter>

      <Chapter eyebrow="Build them" title="Sevenths and extensions, side by side">
        <Prose>
          <p>
            The builder below has both the seventh chords from the previous chapter and
            their full extensions. Toggle a 9, then a ♭9, then a ♯9, then add an 11 or 13.
            Listen for the moment a chord goes from "settled" to "tingling" to "completely
            unhinged". That's the spectrum jazz harmony works on.
          </p>
        </Prose>

        <ChordBuilder families={["seventh", "extension"]} />
      </Chapter>

      <Chapter eyebrow="Melody first" title="Let the tune dictate the tension">
        <Prose>
          <p>
            The most useful way to think about extensions isn't "which one sounds coolest"
            — it's <em>"what does my melody need?"</em>. If the melody note is a D over a C
            chord, that's a 9. The 9 isn't a colour you chose; it's a fact about the music
            already happening. The chord-name simply describes what's there.
          </p>
          <p>
            That's why jazz musicians describe a single melody note as having multiple{" "}
            <em>avatars</em>. A G could be the 5 of a C major chord, the 9 of an F chord,
            the ♭9 of an F♯ dominant 7, the 13 of a B♭ chord, or the 7 of an A♭ major 7.
            Each reading is correct — the harmony just chooses which character that G is
            wearing.
          </p>
        </Prose>

        <TheoryBox label="The composer's question">
          When a melody note sits on a chord, ask: <em>what number is this note in the
          chord?</em> If it's 1, 3, 5 or 7, you're inside the basic chord. If it's 2, 4, or
          6 — you're holding a 9, 11 or 13. Name the chord accordingly and your harmony
          will always match the tune.
        </TheoryBox>
      </Chapter>

      <Chapter eyebrow="What's next" title="Returning to scales — but seen sideways">
        <Prose>
          <p>
            With chords now stretched all the way to 13ths, we've covered the harmonic
            world top to bottom. Time to return to scales — and look at the two ways
            scales relate to each other: <em>relatively</em> (sharing notes, changing root)
            and <em>parallelly</em> (sharing root, changing notes). Modes live at the
            intersection.
          </p>
        </Prose>
        <NoteImage
          src="/notes/music-theory/finals/22b-full-chord-families-with-extensions.png"
          caption="The complete chord-family network — Major / Minor / Diminished / Augmented / Suspended branches, each producing 7ths, 9ths, 11ths, 13ths and altered cousins. This is the entire jazz vocabulary on one page."
        />
      </Chapter>

      <NextUp
        to="/lesson/music-theory-23"
        label="Chapter 23 · Understanding Modes - Relative (Part 1)"
        why="From chord stacks back to scale shapes. Same seven notes, seven different roots — meet the modes seen relatively."
      />
    </LessonShell>
  );
}
